/**
 * city/builder-core.js
 * Tiện ích toán học, bộ sinh ngẫu nhiên, hình học cơ sở và bộ nhớ đệm (Primitive & Cache)
 */

import * as THREE from 'three';

// Bộ sinh số ngẫu nhiên có seed để chi tiết procedural ổn định giữa các lần dựng
function createRng(seed = 1) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str = '') {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Pha sáng (amount > 0) hoặc tối (amount < 0) một mã màu hex trong không gian sRGB
function shade(hex, amount) {
  const n = parseInt(hex.replace('#', ''), 16);
  const target = amount < 0 ? 0 : 255;
  const p = Math.abs(amount);
  const mix = (c) => Math.round((target - c) * p + c);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
}

export { createRng, hashString, shade };

class CoreHolder {
  mat(color, opts = {}) {
    const key = `std|${color}|${JSON.stringify(opts)}`;
    let m = this._matCache.get(key);
    if (!m) {
      // Cảnh không dùng environment map (quá nặng khi render): kim loại gần 100% sẽ đen xỉn,
      // nên giới hạn độ kim loại để inox / chrome vẫn giữ màu sáng
      const o = { ...opts };
      if (o.metalness > 0.35) {
        o.metalness = 0.35;
        o.roughness = Math.max(o.roughness ?? 0.8, 0.3);
      }
      m = new THREE.MeshStandardMaterial({ color, roughness: 0.8, ...o });
      this._matCache.set(key, m);
      this.disposables.push(m);
    }
    return m;
  }

  // Vật liệu tự phát sáng (đèn LED, đèn pha) không phụ thuộc ánh sáng cảnh
  glow(color) {
    const key = `glow|${color}`;
    let m = this._matCache.get(key);
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color });
      this._matCache.set(key, m);
      this.disposables.push(m);
    }
    return m;
  }

  texMat(texture, opts = {}, basic = false) {
    const m = basic
      ? new THREE.MeshBasicMaterial({ map: texture, ...opts })
      : new THREE.MeshStandardMaterial({ map: texture, roughness: 0.85, ...opts });
    this.disposables.push(m);
    return m;
  }

  glassMat(kind = 'sky') {
    const key = `glass|${kind}`;
    let m = this._matCache.get(key);
    if (!m) {
      m = new THREE.MeshStandardMaterial({ map: this.getWindowTexture(kind), roughness: 0.12, metalness: 0.25 });
      this._matCache.set(key, m);
      this.disposables.push(m);
    }
    return m;
  }

  _geo(key, factory) {
    let g = this._geoCache.get(key);
    if (!g) {
      g = factory();
      this._geoCache.set(key, g);
      this.disposables.push(g);
    }
    return g;
  }

  box(parent, w, h, d, material, x = 0, y = 0, z = 0, rot = null, castShadow = true) {
    const geo = this._geo(`b|${w.toFixed(3)}|${h.toFixed(3)}|${d.toFixed(3)}`, () => new THREE.BoxGeometry(w, h, d));
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x, y, z);
    if (rot) mesh.rotation.set(rot[0] || 0, rot[1] || 0, rot[2] || 0);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }

  cyl(parent, rTop, rBot, h, seg, material, x = 0, y = 0, z = 0, rot = null, castShadow = true) {
    const geo = this._geo(`c|${rTop}|${rBot}|${h}|${seg}`, () => new THREE.CylinderGeometry(rTop, rBot, h, seg));
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x, y, z);
    if (rot) mesh.rotation.set(rot[0] || 0, rot[1] || 0, rot[2] || 0);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }

  plane(parent, w, h, material, x = 0, y = 0, z = 0, rot = null) {
    const geo = this._geo(`p|${w}|${h}`, () => new THREE.PlaneGeometry(w, h));
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x, y, z);
    if (rot) mesh.rotation.set(rot[0] || 0, rot[1] || 0, rot[2] || 0);
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }

  // Dây võng dạng parabol: đỉnh tại p0, điểm thấp nhất nằm ngang tại p2
  _sagWire(parent, p0, p2, sag, radius, material) {
    const end = p2.clone();
    end.y -= sag;
    const ctrl = new THREE.Vector3((p0.x + end.x) / 2, end.y, (p0.z + end.z) / 2);
    const curve = new THREE.QuadraticBezierCurve3(p0, ctrl, end);
    const geo = new THREE.TubeGeometry(curve, 18, radius, 4, false);
    this.disposables.push(geo);
    const mesh = new THREE.Mesh(geo, material);
    parent.add(mesh);
    return mesh;
  }
}

export function registerCoreMethods(proto) {
  Object.getOwnPropertyNames(CoreHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = CoreHolder.prototype[name];
    }
  });
}
